import { HoverCard, Text } from '@mantine/core';
import type { RunStatus } from '@prisma/client';
import clsx from 'clsx';
import { match } from 'ts-pattern';
import { prettyDuration } from '~/common/time/duration';
import TimeAgo from '~/components/time/TimeAgo';
import { useProjectId } from '~/hooks/context/useProjectId';

export type TrendDataItem = {
  suiteRunId: string;
  createdAt: Date;
  testResults: Record<
    string,
    {
      testRunId: string;
      status: RunStatus;
      durationMs: number | null;
      attemptCount: number;
    }
  >;
};

type TrendCellProps = {
  testId: string;
  trendData?: TrendDataItem[];
};

// Maps RunStatus to background color for the trend bar
// Defined outside component to avoid recreation on every render
const getStatusColor = (status: RunStatus) => {
  return match(status)
    .with('PASSED', () => 'bg-emerald-500/85')
    .with('FLAKY', () => 'bg-emerald-300/85')
    .with('FAILED', () => 'bg-rose-500/85')
    .with('RUNNING', () => 'bg-amber-500/85')
    .with('CANCELLED', () => 'bg-slate-500/85')
    .with('SKIPPED', () => 'bg-slate-400/85 dark:bg-slate-600/85 border border-slate-500/85 dark:border-slate-400/85')
    .with('QUEUED', () => 'bg-slate-500/85')
    .with('RETRYING', () => 'bg-amber-600/85')
    .with('ERROR', () => 'bg-rose-700/85')
    .exhaustive();
};

export default function TrendCell({ testId, trendData }: TrendCellProps) {
  const projectId = useProjectId();

  // Don't render anything when there's no data (empty array means no historical runs)
  if (trendData?.length === 0) {
    return null;
  }

  // Show skeleton while loading (trendData is undefined)
  if (trendData == null) {
    return (
      <div className="flex min-w-0 shrink-0 items-center gap-0.5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            className="h-4 w-1.5 animate-pulse rounded-sm bg-gray-300 dark:bg-gray-700"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 shrink-0 items-center gap-0.5">
      {/* Newest on left, oldest on right */}
      {trendData.map((suiteRun) => {
        const testResult = suiteRun.testResults[testId];

        // Test wasn't part of this suite run
        if (!testResult) {
          return (
            <div
              key={suiteRun.suiteRunId}
              className="h-4 w-1.5 rounded-sm bg-transparent"
              title="Test not in this run"
            />
          );
        }

        return (
          <HoverCard
            key={suiteRun.suiteRunId}
            width={200}
            shadow="md"
            position="top"
            withArrow
            openDelay={100}
            closeDelay={100}
          >
            <HoverCard.Target>
              <a
                href={`/project/${projectId}/history/g_${suiteRun.suiteRunId}/run/${testResult.testRunId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div
                  className={clsx(
                    'h-4 w-1.5 cursor-pointer rounded-sm transition-opacity hover:opacity-70',
                    getStatusColor(testResult.status),
                  )}
                />
              </a>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Text
                    size="xs"
                    fw={600}
                  >
                    Status
                  </Text>
                  <Text
                    size="xs"
                    className="capitalize"
                  >
                    {testResult.status.toLowerCase()}
                  </Text>
                </div>
                <div className="flex items-center justify-between">
                  <Text
                    size="xs"
                    fw={600}
                  >
                    Started
                  </Text>
                  <Text size="xs">
                    <TimeAgo time={suiteRun.createdAt} />
                  </Text>
                </div>
                {testResult.durationMs != null && (
                  <div className="flex items-center justify-between">
                    <Text
                      size="xs"
                      fw={600}
                    >
                      Duration
                    </Text>
                    <Text size="xs">{prettyDuration(Math.ceil(testResult.durationMs / 1000))}</Text>
                  </div>
                )}
                {testResult.attemptCount > 1 && (
                  <div className="flex items-center justify-between">
                    <Text
                      size="xs"
                      fw={600}
                    >
                      Attempts
                    </Text>
                    <Text size="xs">{testResult.attemptCount}</Text>
                  </div>
                )}
              </div>
            </HoverCard.Dropdown>
          </HoverCard>
        );
      })}
    </div>
  );
}
